import { Navigate, useRoutes } from 'react-router-dom';
import { wrapUseRoutes } from '@sentry/react';
import { AnimationWrapper } from '@mgm/common/wrappers';
import LoginPage from '../pages/welcome/LoginPage';
import ForgotPage from '../pages/welcome/ForgotPage';
import CheckEmailPage from '../pages/welcome/CheckEmailPage';
import InviteTeamPage from '../pages/onboarding/InviteTeamPage';
import RegisterPage from '../pages/onboarding/RegisterPage';
import ResetPassPage from '../pages/welcome/ResetPassPage';
import MainRegisterPage from '../pages/welcome/RegisterPage';

import { MainLayout } from '../layout';
import { ProtectedRoute } from './ProtectedRoute';
import { AttributeStaff } from '../pages/pos/AttributeStaff';
import NotFoundPage from '../pages/NotFoundPage';
import ErrorPage from '../pages/ErrorPage';
import { RoleProtectedRoute } from './RoleProtectedRoute';
import TeamPage from '../pages/team/TeamPage';
import TimesheetsPage from '../pages/sheets/TimesheetsPage';
import OrdersPage from '../pages/OrdersPage';
import SchedulesPage from '../pages/schedules/SchedulesPage';
import PayrollPage from '../pages/PayrollPage';
import HelpPage from '../pages/HelpPage';
import SettingsPage from '../pages/SettingsPage';
import BulkMembersPage from '../pages/team/BulkMembersPage';
import ScheduleBulkShiftsPage from '../pages/schedules/ScheduleBulkShiftsPage';
import TimesheetsMemberPage from '../pages/sheets/TimesheetsMemberPage';
import TasklistsPage from '../pages/TasklistsPage';
import DashboardPage from '../pages/DashboardPage';
import PricingPage from '../pages/onboarding/PricingPage';
import CreatePlanPage from '../pages/commissions/CreatePlanPage';
import CommissionsPage from '../pages/commissions/CommissionsPage';
import { PackageProtectedRoute } from './PackageProtectedRoute';
import CommissionModelPage from '../pages/commissions/CommissionModelPage';
import CommissionTeams from '../pages/commissions/CommissionTeams';
import InventoryPage from '../pages/InventoryPage';
import CalendarPage from '../pages/schedules/CalendarPage';
import TimeoffPage from '../pages/schedules/TimeoffPage';
import CommissionAnalyticsPage from '../pages/analytics/CommissionAnalyticsPage';
import DorAnalyticsPage from '../pages/analytics/DorAnalyticsPage';
import ProductExchangePage from '../pages/ProductExchangePage';
import { CommissionTeamAnalyticsPage } from '../pages/analytics/CommissionTeamAnalyticsPage';
import { ErrorBoundary } from '../components/error/ErrorBoundary';

const useSentryRoutes = wrapUseRoutes(useRoutes);

const InitRoutes = () => useSentryRoutes([
  {
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/dashboard',
        element: (
          <ProtectedRoute>
            <PackageProtectedRoute permissionKey="/dashboard">
              <RoleProtectedRoute perform="visibility" permissionKey="dashboard">
                <AnimationWrapper>
                  <DashboardPage />
                </AnimationWrapper>
              </RoleProtectedRoute>
            </PackageProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/team',
        element: (
          <ProtectedRoute>
            <RoleProtectedRoute perform="visibility" permissionKey="team">
              <AnimationWrapper>
                <TeamPage />
              </AnimationWrapper>
            </RoleProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/team/bulk',
        element: (
          <ProtectedRoute>
            <AnimationWrapper>
              <BulkMembersPage />
            </AnimationWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: '/timesheets',
        element: (
          <ProtectedRoute>
            <RoleProtectedRoute perform="visibility" permissionKey="sheets">
              <AnimationWrapper>
                <TimesheetsPage />
              </AnimationWrapper>
            </RoleProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/schedules/weekly',
        element: (
          <ProtectedRoute>
            <RoleProtectedRoute perform="visibility" permissionKey="schedules">
              <AnimationWrapper>
                <SchedulesPage />
              </AnimationWrapper>
            </RoleProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/schedules/monthly',
        element: (
          <ProtectedRoute>
            <RoleProtectedRoute perform="visibility" permissionKey="schedules">
              <AnimationWrapper>
                <CalendarPage />
              </AnimationWrapper>
            </RoleProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/schedules/timeoff',
        element: (
          <ProtectedRoute>
            <RoleProtectedRoute perform="actions" permissionKey="timeoff" requiredPermission="manage">
              <AnimationWrapper>
                <TimeoffPage />
              </AnimationWrapper>
            </RoleProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/commissions/plans',
        element: (
          <ProtectedRoute>
            <RoleProtectedRoute perform="visibility" permissionKey="commissionPlans">
              <AnimationWrapper>
                <CommissionsPage />
              </AnimationWrapper>
            </RoleProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/commissions/sales',
        element: (
          <ProtectedRoute>
            <RoleProtectedRoute perform="visibility" permissionKey="commissionSales">
              <AnimationWrapper>
                <OrdersPage />
              </AnimationWrapper>
            </RoleProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/commissions/teams',
        element: (
          <ProtectedRoute>
            <PackageProtectedRoute permissionKey="/commissions/teams">
              <AnimationWrapper>
                <CommissionTeams />
              </AnimationWrapper>
            </PackageProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/commissions/models',
        element: (
          <ProtectedRoute>
            <AnimationWrapper>
              <CommissionModelPage />
            </AnimationWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: '/commissions/create',
        element: (
          <ProtectedRoute>
            <AnimationWrapper>
              <CreatePlanPage />
            </AnimationWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: '/orders/:orderId',
        element: (
          <ProtectedRoute>
            <AnimationWrapper>
              <ProductExchangePage />
            </AnimationWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: '/analytics/commission/individual',
        element: (
          <ProtectedRoute>
            <AnimationWrapper>
              <CommissionAnalyticsPage />
            </AnimationWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: '/analytics/commission/teams',
        element: (
          <ProtectedRoute>
            <AnimationWrapper>
              <CommissionTeamAnalyticsPage />
            </AnimationWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: '/analytics/dor',
        element: (
          <ProtectedRoute>
            <AnimationWrapper>
              <DorAnalyticsPage />
            </AnimationWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: '/timesheets/:memberName',
        element: (
          <ProtectedRoute>
            <AnimationWrapper>
              <TimesheetsMemberPage />
            </AnimationWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: '/payroll',
        element: (
          <ProtectedRoute>
            <PackageProtectedRoute permissionKey="/payroll">
              <RoleProtectedRoute perform="visibility" permissionKey="payroll">
                <AnimationWrapper>
                  <PayrollPage />
                </AnimationWrapper>
              </RoleProtectedRoute>
            </PackageProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/inventory',
        element: (
          <ProtectedRoute>
            <RoleProtectedRoute perform="visibility" permissionKey="inventory">
              <AnimationWrapper>
                <InventoryPage />
              </AnimationWrapper>
            </RoleProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/tasklists',
        element: (
          <ProtectedRoute>
            <RoleProtectedRoute perform="visibility" permissionKey="tasklists">
              <AnimationWrapper>
                <TasklistsPage />
              </AnimationWrapper>
            </RoleProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/schedules/bulk',
        element: (
          <ProtectedRoute>
            <AnimationWrapper>
              <ScheduleBulkShiftsPage />
            </AnimationWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: '/settings',
        element: (
          <ProtectedRoute>
            <AnimationWrapper>
              <SettingsPage />
            </AnimationWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: '/help',
        element: (
          <ProtectedRoute>
            <AnimationWrapper>
              <HelpPage />
            </AnimationWrapper>
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <AnimationWrapper>
        <LoginPage />
      </AnimationWrapper>
    ),
  },
  {
    path: '/forgot',
    element: (
      <AnimationWrapper>
        <ForgotPage />
      </AnimationWrapper>
    ),
  },
  {
    path: '/check-email',
    element: (
      <AnimationWrapper>
        <CheckEmailPage />
      </AnimationWrapper>
    ),
  },
  {
    path: '/reset-pass',
    element: (
      <AnimationWrapper>
        <ResetPassPage />
      </AnimationWrapper>
    ),
  },
  {
    path: '/register',
    element: (
      <AnimationWrapper>
        <ErrorBoundary>
          <MainRegisterPage />
        </ErrorBoundary>
      </AnimationWrapper>
    ),
  },
  {
    path: '/onboarding/invite-team',
    element: (
      <AnimationWrapper>
        <ErrorBoundary>
          <InviteTeamPage />
        </ErrorBoundary>
      </AnimationWrapper>
    ),
  },
  {
    path: '/onboarding/pricing',
    element: (
      <AnimationWrapper>
        <ErrorBoundary>
          <PricingPage />
        </ErrorBoundary>
      </AnimationWrapper>
    ),
  },
  {
    path: '/onboarding',
    element: (
      <AnimationWrapper>
        <RegisterPage />
      </AnimationWrapper>
    ),
  },
  {
    path: '/who-helped',
    element: (
      <AnimationWrapper>
        <AttributeStaff />
      </AnimationWrapper>
    ),
  },
  {
    path: 'not-found',
    element: (
      <AnimationWrapper>
        <NotFoundPage />
      </AnimationWrapper>
    ),
  },
  {
    path: '/error',
    element: (
      <AnimationWrapper>
        <ErrorPage />
      </AnimationWrapper>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/not-found" replace />,
  },
]);

export {
  InitRoutes,
};
